import React, { useState, useEffect, useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import axios from 'axios';
import queryString from 'querystring';
import Services from 'components/Services/Services';
import { useQuery } from 'react-query';

export interface AcuityService {
  serviceImage: string;
  serviceType: string;
  serviceTypeId: number;
  serviceUrl: string;
  sortOrder: number;
  serviceCategory?: string;
}

export interface AcuityAppointment {
  date: string;
  datetime: string;
  canceled: boolean;
  id: number;
  type: string;
  confirmationPage: string;
  time: string;
  endTime: string;
}

export interface User {
  userId: number;
  firstName: string;
  lastName: string;
  userType: UserType;
  building: Building;
}

export interface Building {
  serviceTypes: ServiceType[];
}

export interface ServiceType {
  serviceType: number;
  permissionAccessLevel: 'NoAccess' | 'ReadOnly' | 'FullAccess';
}

export enum UserType {
  NonResident = 'Non-Resident',
}

const API_URL = process.env.REACT_APP_BASE_API_URL;

const ServicesContainer = ({ token, ...rest }: Props) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const leaseId = queryString.parse(
    window.location.search
  ).property_unit_lease_id;

  const headers = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data = [], isLoading } = useQuery(
    ['services', leaseId],
    async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/v2/users/me/services${
          leaseId ? `?leaseId=${leaseId}` : ''
        }`,
        headers
      );
      return response.data.Data.filter(
        ({ serviceUrl }: AcuityService) => serviceUrl
      );
    },
    { staleTime: 0, cacheTime: 0 } // Disable caching
  );

  // Fetch user data using React Query with analytics tracking
  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
  } = useQuery(
    ['user', leaseId],
    async () => {
      const response = await axios.get(
        `${API_URL}/livly/users/me${leaseId ? `?leaseId=${leaseId}` : ''}`,
        headers
      );
      return response.data.Data;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      onSuccess: (data) => {
        const { userId } = data;

        //@ts-ignore
        window.analytics.identify(userId);
        //@ts-ignore
        window.analytics.track('Load Services Page');
      },
    }
  );

  // Fetch appointments using React Query
  const {
    data: appointments = [],
    isLoading: isAppointmentsLoading,
    error: appointmentsError,
  } = useQuery(
    ['appointments', leaseId],
    async () => {
      const response = await axios.get(
        `${API_URL}/v2/users/me/appointments`,
        headers
      );
      return response.data.Data;
    },
    {
      staleTime: 0,
      cacheTime: 0,
    }
  );

  // Combine loading and error states
  useEffect(() => {
    if (!isUserLoading && !isAppointmentsLoading && !isLoading) {
      setLoading(false);
    }
    if (userError || appointmentsError) {
      setError('Error loading services');
    }
  }, [
    isUserLoading,
    isAppointmentsLoading,
    isLoading,
    userError,
    appointmentsError,
  ]);

  const onStartBooking = (service: AcuityService) => {
    //@ts-ignore
    window.analytics.track('Start Service Booking', {
      'Service Type': service.serviceType,
    });
  };

  return (
    <Services
      {...rest}
      user={user}
      serviceLists={data}
      appointments={appointments}
      loading={loading}
      error={error}
      token={token}
      onStartBooking={onStartBooking}
    />
  );
};

interface OwnProps {
  token: string;
}

type Props = OwnProps & RouteComponentProps;

export default ServicesContainer;
